import { CareerFocus, CareerFocusWithMetrics } from '../hooks/useCareerFocus';
import { InternshipPhase } from '../components/InternshipDashboard/InternshipDashboardV2';
import { getInternshipDuration, getCurrentWeek } from './internshipDuration';

/**
 * Get a human-readable progress description based on percentage
 * @param progressPercentage - Progress from 0-100
 * @returns Contextual description of progress
 */
export const getProgressDescription = (progressPercentage: number): string => {
  if (progressPercentage <= 10) return "Just getting started";
  if (progressPercentage <= 25) return "Early stages";
  if (progressPercentage <= 50) return "Building momentum";
  if (progressPercentage <= 75) return "Making strong progress";
  if (progressPercentage <= 90) return "Approaching completion";
  return "Final stretch";
};

/**
 * Get phase position context (early/mid/late)
 * @param currentWeek - Current week number
 * @param phase - Current phase
 * @param totalWeeks - Total internship duration
 * @returns Phase position description
 */
export const getPhasePosition = (
  currentWeek: number,
  phase: InternshipPhase,
  totalWeeks: number
): 'early' | 'mid' | 'late' => {
  // For very short internships, simplify to just early/late
  if (totalWeeks <= 4) {
    return currentWeek <= totalWeeks / 2 ? 'early' : 'late';
  }
  
  // Calculate position within current phase
  const phaseProgress = getPhaseProgress(currentWeek, phase, totalWeeks);
  
  if (phaseProgress <= 0.33) return 'early';
  if (phaseProgress <= 0.67) return 'mid';
  return 'late';
};

/**
 * Get progress within current phase (0-1)
 * @param currentWeek - Current week number
 * @param phase - Current phase
 * @param totalWeeks - Total internship duration
 * @returns Progress percentage within phase
 */
export const getPhaseProgress = (
  currentWeek: number,
  phase: InternshipPhase,
  totalWeeks: number
): number => {
  const boundaries = {
    onboarding: { start: 0, end: 0.08 },
    foundation: { start: 0.08, end: 0.25 },
    growth: { start: 0.25, end: 0.58 },
    impact: { start: 0.58, end: 0.83 },
    transition: { start: 0.83, end: 1 }
  };
  
  const overallProgress = currentWeek / totalWeeks;
  const { start, end } = boundaries[phase];
  const phaseLength = end - start;
  
  return Math.min(1, Math.max(0, (overallProgress - start) / phaseLength));
};

/**
 * Get contextual time remaining description
 * @param daysRemaining - Days left in internship
 * @param totalDays - Total internship duration in days
 * @returns Human-readable time remaining
 */
export const getTimeRemainingContext = (daysRemaining: number, totalDays: number): string => {
  const percentageRemaining = (daysRemaining / totalDays) * 100;
  
  if (daysRemaining <= 0) return "Completed";
  if (daysRemaining === 1) return "Last day";
  if (daysRemaining <= 3) return "Final days";
  if (daysRemaining <= 7) return "Last week";
  if (percentageRemaining <= 10) return "Nearly complete";
  if (percentageRemaining <= 25) return "Final quarter";
  if (percentageRemaining <= 50) return "Halfway point";
  
  return `${Math.ceil(daysRemaining / 7)} weeks remaining`;
};

/**
 * Get adaptive milestone descriptions based on internship length
 * @param totalWeeks - Total internship duration
 * @returns Object with milestone descriptions
 */
export const getAdaptiveMilestones = (totalWeeks: number) => {
  // Very short internship (1 month or less)
  if (totalWeeks <= 4) {
    return {
      week1: "Initial setup",
      midpoint: "Core contributions",
      final: "Wrap up & handoff"
    };
  }
  
  // Short internship (2-3 months)
  if (totalWeeks <= 12) {
    return {
      week1: "Onboarding & orientation",
      week2_3: "Learning & foundation building",
      midpoint: "Active contribution phase",
      final_quarter: "Impact & knowledge transfer",
      final: "Reflection & next steps"
    };
  }
  
  // Standard or long internship (3+ months)
  return {
    week1: "Comprehensive onboarding",
    month1: "Foundation & relationship building",
    midpoint: "Leading initiatives",
    month3: "Demonstrating expertise",
    final_month: "Legacy projects & mentoring",
    final: "Transition planning"
  };
};

/**
 * Convert week-based language to phase-based language
 * @param text - Text potentially containing week references
 * @param currentPhase - Current internship phase
 * @param phasePosition - Position within phase (early/mid/late)
 * @returns Text with contextual timing
 */
export const convertToPhaseBasedLanguage = (
  text: string,
  currentPhase: InternshipPhase,
  phasePosition: 'early' | 'mid' | 'late'
): string => {
  // Map of replacements
  const replacements: Record<string, string> = {
    'Week 1': 'Initial days',
    'Week 2': 'Early foundation phase',
    'Weeks 2-3': 'Foundation building period',
    'Weeks 4-7': 'Core development phase',
    'Weeks 8-10': 'Impact demonstration period',
    'Week 11+': 'Transition phase',
    'first week': 'initial period',
    'last week': 'final phase',
    'this week': `this ${phasePosition} ${currentPhase} phase`,
    'next week': 'upcoming period'
  };
  
  let result = text;
  Object.entries(replacements).forEach(([pattern, replacement]) => {
    result = result.replace(new RegExp(pattern, 'gi'), replacement);
  });
  
  return result;
};

/**
 * Get motivational message based on progress
 * @param focus - Career focus object
 * @returns Contextual motivational message
 */
export const getProgressMotivation = (focus: CareerFocusWithMetrics | null): string => {
  if (!focus) return "Start documenting your journey!";
  
  const progress = focus.progress_percentage || 0;
  const currentWeek = getCurrentWeek(focus);
  const totalWeeks = getInternshipDuration(focus);
  
  if (progress <= 10) {
    return "Great start! Focus on learning and building relationships.";
  } else if (progress <= 25) {
    return "You're building a strong foundation. Keep documenting your learnings!";
  } else if (progress <= 50) {
    return "Halfway there! Your contributions are starting to make an impact.";
  } else if (progress <= 75) {
    return "Strong progress! Focus on completing key deliverables.";
  } else if (progress <= 90) {
    return "Almost there! Time to showcase your achievements.";
  } else {
    return "Final stretch! Ensure smooth handoffs and capture your learnings.";
  }
};